import { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { InsertDriveFile } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import MDBox from "@vendor/components/MDBox";
import MenuCard from "@vendor/examples/Cards/MenuCards";
import { MainLayout } from "@shared/ui/MainLayout";
import { useLoading } from "@shared/hooks/useLoading";
import { ErrorAlert } from "@shared/ui/alerts";
import { usePermission } from "@shared/hooks/usePermission";
import { userContainPermission } from "@shared/utils/permission";
import { useMissingHours } from "../hooks/useMissingHours";
import { useMonthlySummary } from "../hooks/useMonthlySummary";
import { useProjectAssignments } from "../hooks/useProjectAssignments";
import { timeEntryKeys } from "../api/timeEntryKeys";
import { MissingHourSummary } from "../components/MissingHourSummary";
import { RegisterHourAutomaticDialog } from "../components/RegisterHourAutomaticDialog";
import { RegisterHourDescriptive } from "../components/RegisterHourDescriptive";
import { ChargedHourDialog } from "../components/ChargedHourDialog";
import { MissingHourDialog } from "../components/MissingHourDialog";
import { ChargeHourJiraDialog } from "../components/ChargeHourJiraDialog";

import type { MissingHoursWeeklySummary } from "../api/timeEntryApi";

export const ManagementHourPage = () => {
  const { t, i18n: { language } } = useTranslation();
  const { loading, thisLoading, notLoading } = useLoading();
  const qc = useQueryClient();

  const [missingHour, setMissingHour] = useState<MissingHoursWeeklySummary | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogHourDescriptive, setOpenDialogHourDescriptive] = useState(false);
  const [openDialogChargedHour, setOpenDialogChargedHour] = useState(false);
  const [openDialogChargedHourInJira, setOpenDialogChargedHourInJira] = useState(false);
  const [openDialogMissingHour, setOpenDialogMissingHour] = useState(false);

  const { permissionList } = usePermission("Perfil de Imputacion");
  const existPermission = (permission: string) =>
    userContainPermission(permission, permissionList);

  const { data: missingHoursData = [], isFetching: fetchingMissingHours } = useMissingHours();
  const { data: projectData = [], isFetching: fetchingMonthlySummary } = useMonthlySummary();

  const { error: assignmentsError, isFetching: fetchingAssignments } = useProjectAssignments();
  useEffect(() => {
    if (assignmentsError) {
      const msg = assignmentsError?.response?.data;
      ErrorAlert(t("errorTitle"), msg?.message ?? t("internalError")).then();
    }
  }, [assignmentsError]);

  const combinedLoading =
    loading || fetchingMissingHours || fetchingMonthlySummary || fetchingAssignments;

  const reloadInitialMissingHour = () =>
    qc.invalidateQueries({ queryKey: timeEntryKeys.missingHours() });

  const reloadProjectData = () =>
    qc.invalidateQueries({ queryKey: timeEntryKeys.monthlySummary() });

  const onOpenDialog = () => setOpenDialog(true);
  const onCloseDialog = () => setOpenDialog(false);

  const onOpenDialogHourDescriptive = () => setOpenDialogHourDescriptive(true);
  const onCloseDialogHourDescriptive = () => setOpenDialogHourDescriptive(false);

  const onOpenDialogChargedHour = () => setOpenDialogChargedHour(true);
  const onCloseDialogChargedHour = () => setOpenDialogChargedHour(false);

  const onOpenDialogChargedHourInJira = () => setOpenDialogChargedHourInJira(true);
  const onCloseDialogChargedHourInJira = () => setOpenDialogChargedHourInJira(false);

  const onOpenDialogMissingHour = (missingHourExtern: MissingHoursWeeklySummary) => {
    setMissingHour(missingHourExtern);
    setOpenDialogMissingHour(true);
  };
  const onCloseDialogMissingHour = () => setOpenDialogMissingHour(false);

  return (
    <MainLayout loading={combinedLoading}>
      <MDBox py={3}>
        <Grid container spacing={3}>
          {existPermission("Imputacion con Comentarios") && (
            <Grid
              onClick={onOpenDialogHourDescriptive}
              size={{ xs: 12, md: 6, lg: 4, xl: 4 }}
            >
              <MDBox mb={1.5}>
                <MenuCard icon={InsertDriveFile} title={t("descriptiveHourLoad")} />
              </MDBox>
            </Grid>
          )}
          {existPermission("Imputacion Automatica") && (
            <Grid onClick={onOpenDialog} size={{ xs: 12, md: 6, lg: 4, xl: 4 }}>
              <MDBox mb={1.5}>
                <MenuCard icon={InsertDriveFile} title={t("automaticHoursLoading")} />
              </MDBox>
            </Grid>
          )}
          {existPermission("Imputacion General") && (
            <Grid
              onClick={onOpenDialogChargedHour}
              size={{ xs: 12, md: 6, lg: 4, xl: 4 }}
            >
              <MDBox mb={1.5}>
                <MenuCard icon={InsertDriveFile} title={t("loadHours")} />
              </MDBox>
            </Grid>
          )}
          {existPermission("Imputacion con Jira") && (
            <Grid
              onClick={onOpenDialogChargedHourInJira}
              size={{ xs: 12, md: 6, lg: 4, xl: 4 }}
            >
              <MDBox mb={1.5}>
                <MenuCard icon={InsertDriveFile} title={t("loadHoursInJira")} />
              </MDBox>
            </Grid>
          )}
        </Grid>

        <MissingHourSummary
          thisLoading={thisLoading}
          notLoading={notLoading}
          openDialog={onOpenDialogMissingHour}
          data={missingHoursData}
          t={t}
          language={language}
          projectData={projectData}
          reloadProjectData={reloadProjectData}
          reloadInitialMissingHour={reloadInitialMissingHour}
        />

        <RegisterHourAutomaticDialog
          handleClose={onCloseDialog}
          open={openDialog}
          loading={thisLoading}
          notLoading={notLoading}
          reloadInitialMissingHour={reloadInitialMissingHour}
          t={t}
        />

        <RegisterHourDescriptive
          handleClose={onCloseDialogHourDescriptive}
          open={openDialogHourDescriptive}
          loading={thisLoading}
          notLoading={notLoading}
          reloadInitialMissingHour={reloadInitialMissingHour}
          t={t}
        />

        <ChargedHourDialog
          handleClose={onCloseDialogChargedHour}
          open={openDialogChargedHour}
          loading={thisLoading}
          notLoading={notLoading}
          reloadInitialMissingHour={reloadInitialMissingHour}
          t={t}
        />

        <MissingHourDialog
          handleClose={onCloseDialogMissingHour}
          open={openDialogMissingHour}
          loading={thisLoading}
          notLoading={notLoading}
          missingHour={missingHour}
          reloadInitialMissingHour={reloadInitialMissingHour}
          t={t}
        />

        <ChargeHourJiraDialog
          handleClose={onCloseDialogChargedHourInJira}
          open={openDialogChargedHourInJira}
          loading={thisLoading}
          notLoading={notLoading}
          reloadInitialMissingHour={reloadInitialMissingHour}
          t={t}
        />
      </MDBox>
    </MainLayout>
  );
};
